import { Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";

const CASES = [
  {
    sector: "Plumbing · NSW",
    title: "+212% quote-to-job conversion in 4 months",
    before: "1990s brochure website. Owner taking quotes from a notes app at lunch. Follow-up happened if-and-when. After-hours leads disappeared. Same-day quote response rate: 28%.",
    after: "New conversion-focused site. AI chat for after-hours. CRM with automated quote follow-up (24h SMS, 72h call, 7-day final). Same-day quote response rate: 96%.",
    metrics: [
      { k: "+212%", v: "Quote-to-job conversion" },
      { k: "22 hrs/wk", v: "Returned to the owner" },
      { k: "+47%", v: "Monthly revenue in 4 months" },
    ],
    summary: "The largest gain came from automated quote follow-up, the system simply chased every quote the team had been letting slip.",
  },
  {
    sector: "Electrical · QLD",
    title: "Doubled monthly bookings without doubling ad spend",
    before: "Decent existing ad campaigns but a slow, generic website. Leads landed and bounced. Lead-to-call response time: ~3.5 hours. 40% of after-hours leads never received a callback.",
    after: "Rebuilt landing experience. Missed-call SMS auto-response wired in. AI assistant handling after-hours qualifying & booking. Average response time: 4 minutes.",
    metrics: [
      { k: "2.1×", v: "Monthly booked jobs" },
      { k: "−87%", v: "Lead response time" },
      { k: "0", v: "Increase in ad spend" },
    ],
    summary: "Same money in. More than twice the work out. Speed-to-lead and a clear, mobile-first booking flow did the heavy lifting.",
  },
  {
    sector: "Commercial Cleaning · VIC",
    title: "Replaced 3 part-time admins with one system",
    before: "Owner-operator with three part-time admin staff fielding bookings, invoices, scheduling. Spreadsheets everywhere. No view of margin by job type. Owner working 70+ hour weeks.",
    after: "CRM + dashboards + outsourced execution team. Admin work consolidated, automated where possible, run by our team where not. Margin-by-job visibility unlocked pricing changes.",
    metrics: [
      { k: "−3 FTE", v: "Internal admin headcount" },
      { k: "+9 pts", v: "Net margin" },
      { k: "<50 hrs", v: "Owner working week" },
    ],
    summary: "The dashboards exposed two unprofitable contract types that the owner had been carrying for years. Repricing those alone paid back the system.",
  },
  {
    sector: "HVAC · SA",
    title: "Booked Q3 in full by end of Q2",
    before: "Strong word-of-mouth but no system to capture or convert online interest. Reviews scattered across Google, Facebook, paper feedback forms.",
    after: "Centralised reviews engine (automated post-job SMS request). Modern site with same-day-booking flow. Lead capture tied to local-SEO content.",
    metrics: [
      { k: "5.0", v: "Average Google rating" },
      { k: "+340%", v: "Google reviews in 6 mo" },
      { k: "12 wks", v: "Forward pipeline coverage" },
    ],
    summary: "Reviews compound. Once the review engine was running, organic enquiries doubled inside a quarter without any extra ad spend.",
  },
];

const FAQS = [
  {
    q: "Why are the case studies anonymised?",
    a: "Most of our clients prefer to keep their playbook off public record, the competitive advantage of the growth system erodes the more it's discussed in their local market. Real names and references are available under mutual NDA after the audit call.",
  },
  {
    q: "Are these results typical?",
    a: "They're typical for clients who complete the build and run the system as designed for at least 90 days. Outcomes outside that timeline (or with partial scope) vary. We'll show you the realistic range for your trade during the audit.",
  },
  {
    q: "What's the smallest business you'd work with?",
    a: "We typically work with businesses doing $500k+ in annual revenue. Below that, the ROI math gets harder. We're happy to point earlier-stage operators to lighter alternatives.",
  },
  {
    q: "Have you worked in my specific trade?",
    a: "Probably, the system is built to be trade-agnostic with trade-specific configuration. The audit identifies any genuine gaps in our experience and we tell you straight if we're not the right fit.",
  },
];

export default function CaseStudies() {
  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="case-hero">
        <div className="overline">Case studies</div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          Selected transformations.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          Anonymised and indicative. Real names, references, and detailed
          write-ups available on request under mutual NDA.
        </p>
      </section>

      <section className="section-y" data-testid="case-list">
        <div className="container-x space-y-6">
          {CASES.map((c, i) => (
            <Reveal key={c.title} delay={i * 60}>
              <article className="surface surface-hover p-8 md:p-12" data-testid={`case-${i}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                  <div className="lg:col-span-5">
                    <div className="flex items-center justify-between">
                      <span className="overline">{c.sector}</span>
                      <ArrowUpRight className="w-4 h-4 text-zinc-500" />
                    </div>
                    <h2 className="mt-6 text-2xl md:text-3xl font-display font-light tracking-tight text-balance">
                      {c.title}
                    </h2>
                    <p className="mt-5 text-zinc-300 leading-relaxed">{c.summary}</p>

                    <div className="mt-8 grid grid-cols-3 gap-px bg-white/[0.05]">
                      {c.metrics.map((m) => (
                        <div key={m.v} className="bg-ink p-4">
                          <div className="font-display text-xl md:text-2xl tracking-tight text-balance">{m.k}</div>
                          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
                            {m.v}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.05]">
                    <div className="bg-ink p-6 md:p-8">
                      <div className="overline text-zinc-500">Before</div>
                      <p className="mt-5 text-zinc-400 leading-relaxed text-[15px]">{c.before}</p>
                    </div>
                    <div className="bg-ink p-6 md:p-8 relative overflow-hidden">
                      <div
                        aria-hidden
                        className="absolute -top-16 -right-16 w-64 h-64 rounded-full"
                        style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.12), transparent 70%)" }}
                      />
                      <div className="relative">
                        <div className="overline">After</div>
                        <p className="mt-5 text-zinc-200 leading-relaxed text-[15px]">{c.after}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="container-x mt-16">
          <div className="surface p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <Lock className="w-5 h-5 text-gold mt-1" />
              <div>
                <h3 className="font-display text-xl tracking-tight">Want the full write-ups?</h3>
                <p className="mt-2 text-zinc-400 text-sm max-w-xl">
                  Detailed case studies, full P&amp;L impact, methodology,
                  exact stack and live references, are available on the audit
                  call under mutual NDA.
                </p>
              </div>
            </div>
            <Link to="/contact" className="btn-gold" data-testid="case-cta-book">
              Book the Growth Audit
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="case-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="On the case studies."
              description="The questions every smart operator asks before believing the numbers."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="case-faq" />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
